import { apiService } from './api';

export interface RegisterPreviewRow {
  row: number;
  location_path: string;
  name: string;
  asset_type: string;
  action: 'create' | 'update' | 'unchanged' | 'error';
  message: string;
}
export interface RegisterReport {
  dry_run: boolean;
  created: number;
  updated: number;
  unchanged: number;
  locations_created: number;
  errors: { row: number; column: string; message: string }[];
  warnings: string[];
  preview: RegisterPreviewRow[];
}
const columns = 'location_path,name,asset_type,parent_asset,description,manufacturer,model,serial_number,install_year,risk_likelihood,risk_significance,notes';

function download(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const assetRegisterService = {
  async import(facilityId: string, file: File, dryRun: boolean): Promise<RegisterReport> {
    const body = new FormData();
    body.append('file', file);
    body.append('dry_run', String(dryRun));
    const response = await fetch(`${apiService.getBaseUrl()}/facilities/${facilityId}/assets/import`, {
      method: 'POST', headers: await apiService.authHeaders(), body,
    });
    const result = await response.json();
    if ((response.ok || response.status === 422) && Array.isArray(result.preview) && Array.isArray(result.errors)) return result;
    throw new Error(typeof result.detail === 'string' ? result.detail : 'Unable to import the asset register.');
  },
  async export(facilityId: string): Promise<void> {
    const response = await fetch(`${apiService.getBaseUrl()}/facilities/${facilityId}/assets/export.csv`, {
      headers: await apiService.authHeaders(),
    });
    if (!response.ok) throw new Error('Unable to export the asset register.');
    const filename = response.headers.get('Content-Disposition')?.match(/filename="([^"]+)"/)?.[1] || 'facility-assets.csv';
    download(await response.blob(), filename);
  },
  template(): void {
    const examples = [
      'Plant Building > Roof > Tower enclosure,CT-1,cooling_tower,,Cooling tower,Evapco,,EXAMPLE-001,2020,,,',
      'Plant Building > Roof > Tower enclosure,CT-1 bleed valve,valve,CT-1,Automatic bleed valve,Enware,,EXAMPLE-002,2020,,,',
      'Main Building > Level 1 > Ward 1A,TMV-1A-01,tmv,,Bathroom mixing valve,Enware,Aquablend 1500,EXAMPLE-003,2021,,,',
    ];
    download(new Blob([`${columns}\r\n${examples.join('\r\n')}\r\n`], { type: 'text/csv;charset=utf-8' }), 'asset-register-template.csv');
  },
};
